import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLoaderData, useOutletContext } from "react-router-dom";
import { isArray, debounce } from "lodash";
import { getEntities } from "../API";

function normalizeResponse(response) {
  if (Array.isArray(response)) return response;
  if (response?._embedded) return Object.values(response._embedded).flat();
  if (response?.content) return response.content;
  return [];
}

function getSortValue(entity, field) {
  if (field.isObject) return entity[field.propertyName]?.name ?? "";
  if (isArray(field.propertyName)) return field.propertyName.map((p) => entity[p]).join(" ");
  return entity[field.propertyName] ?? "";
}

function Entities({ shema }) {
  const serverResponse = useLoaderData();

  const entities = normalizeResponse(serverResponse);
  const [filteredEntities, setFilteredEntities] = useState(entities);
  const [totalPages, setTotalPages] = useState(serverResponse?.totalPages ?? null);
  const [currentPage, setCurrentPage] = useState(serverResponse?.number ?? 0);
  const [sortConfig, setSortConfig] = useState({ fieldIndex: null, dir: "asc" });

  const [searchText, setSearchText] = useOutletContext();
  const [pageSize, setPageSize] = useState(25);

  const filterFields = shema.fields.filter((f) => f.filter);
  const initialSelections = Object.fromEntries(filterFields.map((f) => [f.propertyName, ""]));
  const [filterSelections, setFilterSelections] = useState(initialSelections);
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    filterFields.forEach((field) => {
      getEntities(field.filterRoute).then((response) => {
        setFilterOptions((prev) => ({ ...prev, [field.propertyName]: normalizeResponse(response) }));
      });
    });
    if (searchText) search(searchText, 0, initialSelections);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const navigate = useNavigate();

  const userClick = (id) => navigate(`${id}`);

  const debounceSearch = useCallback(debounce((term, filters, size) => search(term, 0, filters, size), 500), []);

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    debounceSearch(e.target.value, filterSelections, pageSize);
  };

  const handleFilter = (propertyName, value) => {
    const newSelections = { ...filterSelections, [propertyName]: value };
    setFilterSelections(newSelections);
    search(searchText, 0, newSelections, pageSize);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    search(searchText, 0, filterSelections, newSize);
  };

  const handleSort = (fieldIndex) => {
    const field = shema.fields[fieldIndex];
    if (field.isArray || field.renderCell) return;
    setSortConfig((prev) =>
      prev.fieldIndex === fieldIndex
        ? { fieldIndex, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { fieldIndex, dir: "asc" }
    );
  };

  const sortedEntities = useMemo(() => {
    if (sortConfig.fieldIndex === null) return filteredEntities;
    const field = shema.fields[sortConfig.fieldIndex];
    return [...filteredEntities].sort((a, b) => {
      const av = getSortValue(a, field);
      const bv = getSortValue(b, field);
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
      return sortConfig.dir === "asc" ? cmp : -cmp;
    });
  }, [filteredEntities, sortConfig, shema.fields]);

  function buildSearchParams(searchTerm, page, filters, size) {
    const searchFields = shema.fields.filter((f) => f.search);
    let params = `?page=${page}&size=${size}`;

    if (searchTerm) {
      const searchPart = searchFields.reduce(
        (acc, field) => acc + field.propertyName + ":" + searchTerm + ",",
        ""
      );
      params = `?search=${searchPart}&page=${page}`;
    }

    const activeFilters = Object.entries(filters ?? {}).filter(([, v]) => v !== "");
    if (activeFilters.length > 0) {
      const filterPart = activeFilters.reduce(
        (acc, [key, val]) => acc + key + ":" + val + ",",
        ""
      );
      params += `&filter=${filterPart}`;
    }

    return params;
  }

  function search(searchTerm, page, filters, size) {
    const searchFields = shema.fields.filter((f) => f.search);

    if (shema.searchFromServer) {
      getEntities(shema.searchRoute, buildSearchParams(searchTerm, page, filters, size)).then((response) => {
        setFilteredEntities(normalizeResponse(response));
        setTotalPages(response?.totalPages ?? null);
        setCurrentPage(response?.number ?? 0);
      });
    } else {
      const filteredItems = entities.filter((entity) => {
        for (let index = 0; index < searchFields.length; index++) {
          const f = searchFields[index];
          let val;
          if (f.isObject) val = entity[f.propertyName].name;
          else val = entity[f.propertyName];
          if (val.toLowerCase().includes(searchTerm.toLowerCase())) return true;
        }
        return false;
      });
      setFilteredEntities(filteredItems);
    }
  }

  const handlePageChange = (newPage) => search(searchText, newPage, filterSelections, pageSize);

  function getPageNumbers(current, total) {
    const delta = 2;
    const window = [];
    for (let i = Math.max(0, current - delta); i <= Math.min(total - 1, current + delta); i++) {
      window.push(i);
    }
    const result = [];
    if (window[0] > 0) {
      result.push(0);
      if (window[0] > 1) result.push("...");
    }
    result.push(...window);
    if (window[window.length - 1] < total - 1) {
      if (window[window.length - 1] < total - 2) result.push("...");
      result.push(total - 1);
    }
    return result;
  }

  return (
    <div className="flex-grow-1 p-4 overflow-auto">
      <div className="container mb-3">
        <div className="d-flex gap-2 align-items-center">
          <input
            className="form-control flex-grow-1"
            type="search"
            value={searchText}
            onChange={handleSearch}
            placeholder="Search"
            aria-label="Search"
          />
          {filterFields.map((field) => (
            <select
              key={field.propertyName}
              className="form-select w-auto"
              value={filterSelections[field.propertyName]}
              onChange={(e) => handleFilter(field.propertyName, e.target.value)}
            >
              <option value="">All {field.name}s</option>
              {(filterOptions[field.propertyName] ?? []).map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.name}</option>
              ))}
            </select>
          ))}
          <select
            className="form-select w-auto"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button className="btn btn-primary" onClick={() => navigate("new")}>
            New
          </button>
        </div>
      </div>
      <table className="table table-hover">
        <thead>
          <tr>
            {shema.fields.map((field, i) => {
              const sortable = !field.isArray && !field.renderCell;
              const isActive = sortConfig.fieldIndex === i;
              return (
                <th
                  key={i}
                  className={sortable ? "sortable-header" : ""}
                  onClick={sortable ? () => handleSort(i) : undefined}
                >
                  {field.name}
                  {sortable && (
                    <span className="sort-icon">
                      {isActive ? (sortConfig.dir === "asc" ? " ▲" : " ▼") : " ⇅"}
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedEntities.map((entity) => (
            <tr role="button" key={entity.id} onClick={() => userClick(entity.id)}>
              {shema.fields.map((field, i) => {
                if (field.renderCell) {
                  return field.renderCell(entity, i);
                } else if (field.isArray) {
                  return (
                    <td key={i}>
                      {entity[field.propertyName].map((p, j) => (
                        <div key={j}><small>{p.name}</small></div>
                      ))}
                    </td>
                  );
                } else if (field.isObject) {
                  return <td key={i}>{entity[field.propertyName]?.name}</td>;
                } else {
                  if (isArray(field.propertyName)) {
                    return (
                      <td key={i}>
                        {field.propertyName.map((property) => entity[property] + " ")}
                      </td>
                    );
                  } else {
                    return <td key={i}>{field.format ? field.format(entity[field.propertyName]) : entity[field.propertyName]}</td>;
                  }
                }
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <nav>
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                Previous
              </button>
            </li>
            {getPageNumbers(currentPage, totalPages).map((p, i) =>
              p === "..." ? (
                <li key={`dots-${i}`} className="page-item disabled">
                  <span className="page-link">…</span>
                </li>
              ) : (
                <li key={p} className={`page-item ${p === currentPage ? "active" : ""}`}>
                  <button className="page-link" onClick={() => handlePageChange(p)}>
                    {p + 1}
                  </button>
                </li>
              )
            )}
            <li className={`page-item ${currentPage === totalPages - 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
}

export default Entities;
